import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Download, FileText } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Card } from '../components/Card.tsx';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalHealthBanner } from '../components/PortalHealthBanner.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface PortalContact {
  id: string;
  organizationId: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

interface PeriodSummary {
  id: string;
  periodStart: string;
  periodEnd: string;
  grantedHours: number;
  consumedHours: number;
  remainingHours: number;
  rolloverInHours?: number;
  rolloverOutHours?: number | null;
  forfeitedHours?: number | null;
  status?: 'open' | 'closed';
  isFinal?: boolean;
}

interface BillingResponse {
  period: PeriodSummary | null;
  monthKey: string | null;
}

interface HistoryPeriod {
  periodId: string;
  periodStart: string;
  periodEnd: string;
  monthKey: string;
  status: 'open' | 'closed';
  isFinal: boolean;
  baseHours: number;
  adHocHours: number;
  rolloverInHours: number;
  consumedHours: number;
  rolloverOutHours: number | null;
  forfeitedHours: number | null;
}

interface HistoryResponse {
  periods: HistoryPeriod[];
}

interface OrgAttachment {
  id: string;
  organizationId: string;
  kind: 'agreement' | 'nda' | 'other';
  filename: string;
  mimeType: string;
  byteSize: number;
  signedOn: string | null;
  label: string | null;
  source: 'manual' | 'qwilr_webhook';
  createdAt: string;
}

const ATTACHMENT_KIND_LABEL: Record<OrgAttachment['kind'], string> = {
  agreement: 'Agreement',
  nda: 'NDA',
  other: 'Other',
};

const ATTACHMENT_KIND_PILL: Record<OrgAttachment['kind'], string> = {
  agreement: 'bg-[var(--color-t3-blue)]/10 text-[var(--color-t3-blue)]',
  nda: 'bg-[var(--color-t3-purple)]/10 text-[var(--color-t3-purple)]',
  other: 'bg-[var(--color-surface)] text-[var(--color-muted)]',
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-');
  if (!y || !m) return key;
  const idx = Number(m) - 1;
  if (idx < 0 || idx > 11) return key;
  return `${MONTH_NAMES[idx]} ${y}`;
}

function buildMonthOptions(): string[] {
  const keys: string[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    keys.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return keys;
}

export function PortalBillingPage(): React.ReactElement {
  useDocumentTitle('Billing');
  const navigate = useNavigate();
  const monthOptions = useMemo(buildMonthOptions, []);
  const [contact, setContact] = useState<PortalContact | null>(null);
  const [contactLoaded, setContactLoaded] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthKey());
  const [data, setData] = useState<BillingResponse | null>(null);
  const [history, setHistory] = useState<HistoryPeriod[] | null>(null);
  const [attachments, setAttachments] = useState<OrgAttachment[] | null>(null);
  const [attachmentsError, setAttachmentsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ contact: PortalContact | null }>('/api/portal/auth/me')
      .then((r) => {
        if (!r.contact) {
          navigate('/login', { replace: true, state: { from: '/portal/billing' } });
          return;
        }
        setContact(r.contact);
      })
      .catch(() => {
        navigate('/login', { replace: true, state: { from: '/portal/billing' } });
      })
      .finally(() => setContactLoaded(true));
  }, [navigate]);

  useEffect(() => {
    if (!contact?.isOrgBilling) return;
    apiFetch<HistoryResponse>('/api/portal/tickets/_meta/billing-history?months=12')
      .then((r) => setHistory(r.periods))
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load history.');
      });
    apiFetch<{ attachments: OrgAttachment[] }>('/api/portal/organization/attachments')
      .then((r) => setAttachments(r.attachments))
      .catch((err: unknown) => {
        setAttachmentsError(
          err instanceof ApiError ? err.message : 'Failed to load contracts.',
        );
      });
  }, [contact]);

  useEffect(() => {
    if (!contact?.isOrgBilling) return;
    let cancelled = false;
    setError(null);
    const monthQuery = selectedMonth === 'all' ? '' : `?month=${selectedMonth}`;
    apiFetch<BillingResponse>(`/api/portal/tickets/_meta/billing-summary${monthQuery}`)
      .then((b) => {
        if (cancelled) return;
        setData(b);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load billing summary.');
      });
    return () => {
      cancelled = true;
    };
  }, [contact, selectedMonth]);

  if (!contactLoaded) {
    return (
      <div className="bg-white">
        <PortalHealthBanner />
        <PortalNavBar contact={null} active="billing" />
        <main className="mx-auto max-w-6xl px-6 py-10 sm:px-8">
          <p className="text-[var(--color-muted)]">Loading…</p>
        </main>
      </div>
    );
  }

  if (!contact) return <></>;

  if (!contact.isOrgBilling) {
    return (
      <div className="bg-white">
        <PortalHealthBanner />
        <PortalNavBar contact={contact} active="billing" />
        <main className="mx-auto max-w-6xl px-6 py-10 sm:px-8">
          <Card>
            <p className="text-sm text-[var(--color-muted)]">
              Billing is available only to contacts with the <strong>Org billing</strong> role.
              Ask your T3 admin or organization admin to update your role.
            </p>
            <p className="mt-3">
              <Link to="/portal" className="text-sm text-[var(--color-t3-blue)] hover:underline">
                ← Back to help center
              </Link>
            </p>
          </Card>
        </main>
      </div>
    );
  }

  const isAllTime = selectedMonth === 'all';
  const scopeLabel = isAllTime ? 'all time' : monthLabel(selectedMonth);

  return (
    <div className="bg-white">
      <PortalHealthBanner />
      <PortalNavBar contact={contact} active="billing" />

      <main className="mx-auto max-w-6xl space-y-6 px-6 py-10 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Link to="/portal" className="text-sm text-[var(--color-muted)] hover:underline">
              ← Help center
            </Link>
            <h1 className="mt-1 font-display text-3xl font-bold text-[var(--color-header)]">
              Billing
            </h1>
            <p className="mt-1 text-sm text-[var(--color-muted)]">
              Block-hour subscription, period balance, and rollover history. For per-ticket and
              per-project hours, see{' '}
              <Link to="/portal/reports" className="text-[var(--color-t3-blue)] hover:underline">
                Reports
              </Link>
              .
            </p>
          </div>
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-[var(--color-muted)]">View</span>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="mt-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
            >
              {monthOptions.map((k, i) => (
                <option key={k} value={k}>
                  {i === 0 ? `Current month — ${monthLabel(k)}` : monthLabel(k)}
                </option>
              ))}
              <option value="all">All time</option>
            </select>
          </label>
        </div>

        {error && (
          <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
            <p className="text-sm text-[var(--color-danger)]">{error}</p>
          </Card>
        )}

        {isAllTime ? (
          <Card>
            <p className="text-sm text-[var(--color-muted)]">
              Switch the view to a specific month for period-level granted, used, rollover, and
              forfeit details.
            </p>
          </Card>
        ) : !data ? (
          <p className="text-sm text-[var(--color-muted)]">Loading period…</p>
        ) : data.period ? (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <Stat
                label="Granted this period"
                value={`${data.period.grantedHours.toFixed(2)}h`}
                sub={`${data.period.periodStart} – ${data.period.periodEnd}`}
              />
              <Stat
                label="Used"
                value={`${data.period.consumedHours.toFixed(2)}h`}
                sub={`${data.period.grantedHours > 0 ? Math.round((data.period.consumedHours / data.period.grantedHours) * 100) : 0}% of granted`}
              />
              <Stat
                label="Remaining"
                value={`${data.period.remainingHours.toFixed(2)}h`}
                sub={data.period.remainingHours <= 0 ? 'depleted' : 'available'}
                danger={data.period.remainingHours <= 0}
              />
            </div>

            <Card>
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-[var(--color-muted)]">Period progress — {scopeLabel}</span>
                <span>
                  {data.period.consumedHours.toFixed(2)}h of {data.period.grantedHours.toFixed(2)}h
                </span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--color-border)]">
                <div
                  className={
                    'h-full ' +
                    (data.period.consumedHours / Math.max(1, data.period.grantedHours) >= 0.8
                      ? 'bg-[var(--color-danger)]'
                      : 'bg-[var(--color-t3-blue)]')
                  }
                  style={{
                    width: `${
                      data.period.grantedHours > 0
                        ? Math.min(100, (data.period.consumedHours / data.period.grantedHours) * 100)
                        : 0
                    }%`,
                  }}
                />
              </div>
              {data.period.status === 'closed' && (
                <div className="mt-3 grid gap-2 border-t border-[var(--color-border)] pt-3 text-xs sm:grid-cols-3">
                  <KV label="Rolled in" value={fmtH(data.period.rolloverInHours)} />
                  <KV label="Rolled out" value={fmtH(data.period.rolloverOutHours)} />
                  <KV
                    label="Forfeited"
                    value={fmtH(data.period.forfeitedHours)}
                    danger={(data.period.forfeitedHours ?? 0) > 0}
                  />
                </div>
              )}
            </Card>
          </>
        ) : (
          <Card>
            <p className="text-center text-sm text-[var(--color-muted)]">
              No block-hour subscription was active in {monthLabel(selectedMonth)}.
            </p>
          </Card>
        )}

        <HistorySection history={history} />

        <ContractsSection attachments={attachments} error={attachmentsError} />
      </main>

      <PortalFooter />
    </div>
  );
}

function HistorySection({ history }: { history: HistoryPeriod[] | null }): React.ReactElement | null {
  if (history === null) return <p className="text-sm text-[var(--color-muted)]">Loading history…</p>;
  if (history.length === 0) {
    return (
      <Card>
        <p className="text-center text-sm text-[var(--color-muted)]">
          Period history will appear here once your first month closes.
        </p>
      </Card>
    );
  }

  const ascending = [...history].reverse();
  const maxConsumed = Math.max(
    ...ascending.map((p) => Math.max(p.consumedHours, p.baseHours + p.adHocHours + p.rolloverInHours)),
    1,
  );

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-[var(--color-header)]">Period history</h2>
        <CsvLink href="/api/portal/tickets/_meta/billing-history.csv?months=12">
          Download CSV
        </CsvLink>
      </div>
      <Card>
        <div className="space-y-1.5">
          {ascending.map((p) => {
            const granted = p.baseHours + p.adHocHours + p.rolloverInHours;
            const usedPct = (p.consumedHours / maxConsumed) * 100;
            const grantedPct = (granted / maxConsumed) * 100;
            const overUsed = p.consumedHours > granted;
            return (
              <div key={p.periodId} className="grid grid-cols-12 items-center gap-2 text-xs">
                <div className="col-span-2 text-[var(--color-muted)]">{monthLabel(p.monthKey)}</div>
                <div className="col-span-7 relative h-3 rounded-full bg-[var(--color-border)]">
                  <div
                    className="absolute left-0 top-0 h-full rounded-full bg-[var(--color-t3-blue)]/25"
                    style={{ width: `${grantedPct}%` }}
                    title={`Granted ${granted.toFixed(2)}h`}
                  />
                  <div
                    className={
                      'absolute left-0 top-0 h-full rounded-full ' +
                      (overUsed ? 'bg-[var(--color-danger)]' : 'bg-[var(--color-t3-blue)]')
                    }
                    style={{ width: `${usedPct}%` }}
                    title={`Used ${p.consumedHours.toFixed(2)}h`}
                  />
                </div>
                <div className="col-span-3 flex justify-end gap-3 font-mono text-[var(--color-muted)]">
                  <span>{p.consumedHours.toFixed(2)}h used</span>
                  <span>/ {granted.toFixed(2)}h</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left uppercase tracking-wide text-[var(--color-muted)]">
              <tr>
                <th className="py-2 pr-3">Month</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3 text-right">Base</th>
                <th className="py-2 pr-3 text-right">Ad-hoc</th>
                <th className="py-2 pr-3 text-right">Rolled in</th>
                <th className="py-2 pr-3 text-right">Used</th>
                <th className="py-2 pr-3 text-right">Rolled out</th>
                <th className="py-2 pr-3 text-right">Forfeited</th>
              </tr>
            </thead>
            <tbody>
              {history.map((p) => (
                <tr key={p.periodId} className="border-t border-[var(--color-border)]">
                  <td className="py-2 pr-3">{monthLabel(p.monthKey)}</td>
                  <td className="py-2 pr-3 capitalize">
                    {p.status}
                    {p.isFinal ? ' · final' : ''}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono">{p.baseHours.toFixed(2)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{p.adHocHours.toFixed(2)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{p.rolloverInHours.toFixed(2)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{p.consumedHours.toFixed(2)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{fmtH(p.rolloverOutHours)}</td>
                  <td
                    className={
                      'py-2 pr-3 text-right font-mono ' +
                      ((p.forfeitedHours ?? 0) > 0 ? 'text-[var(--color-danger)]' : '')
                    }
                  >
                    {fmtH(p.forfeitedHours)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function ContractsSection({
  attachments,
  error,
}: {
  attachments: OrgAttachment[] | null;
  error: string | null;
}): React.ReactElement {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-[var(--color-header)]">Signed agreements & docs</h2>
        <span className="text-xs text-[var(--color-muted)]">
          Auto-uploaded when you sign a Qwilr agreement.
        </span>
      </div>
      <Card>
        {error ? (
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        ) : attachments === null ? (
          <p className="text-sm text-[var(--color-muted)]">Loading…</p>
        ) : attachments.length === 0 ? (
          <p className="text-center text-sm text-[var(--color-muted)]">
            No signed documents on file yet.
          </p>
        ) : (
          <ul className="divide-y divide-[var(--color-border)]">
            {attachments.map((a) => (
              <li key={a.id} className="flex items-center gap-3 py-2.5">
                <FileText className="h-4 w-4 shrink-0 text-[var(--color-muted)]" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${ATTACHMENT_KIND_PILL[a.kind]}`}
                    >
                      {ATTACHMENT_KIND_LABEL[a.kind]}
                    </span>
                    <span className="truncate font-medium text-[var(--color-header)]">{a.filename}</span>
                    {a.label && (
                      <span className="text-xs text-[var(--color-muted)]">· {a.label}</span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-[var(--color-muted)]">
                    {a.signedOn ? `Signed ${a.signedOn} · ` : ''}
                    {formatBytes(a.byteSize)}
                  </div>
                </div>
                <a
                  href={`/api/portal/organization/attachments/${a.id}/download`}
                  className="inline-flex shrink-0 items-center gap-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 py-1 text-xs font-semibold text-[var(--color-header)] hover:bg-[var(--color-surface)]"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function CsvLink({ href, children }: { href: string; children: React.ReactNode }): React.ReactElement {
  return (
    <a
      href={href}
      className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-1 text-xs text-[var(--color-header)] hover:bg-[var(--color-surface)]"
    >
      {children}
    </a>
  );
}

function KV({
  label,
  value,
  danger,
}: {
  label: string;
  value: string;
  danger?: boolean;
}): React.ReactElement {
  return (
    <div>
      <div className="text-[var(--color-muted)]">{label}</div>
      <div className={'font-mono ' + (danger ? 'text-[var(--color-danger)]' : 'text-[var(--color-header)]')}>
        {value}
      </div>
    </div>
  );
}

function fmtH(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(2)}h`;
}

function Stat({
  label,
  value,
  sub,
  danger,
}: {
  label: string;
  value: string;
  sub?: string;
  danger?: boolean;
}): React.ReactElement {
  return (
    <Card className={danger ? 'border-[var(--color-danger)]/40' : ''}>
      <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">{label}</div>
      <div className={`mt-1 text-3xl font-bold ${danger ? 'text-[var(--color-danger)]' : 'text-[var(--color-header)]'}`}>
        {value}
      </div>
      {sub && <div className="mt-0.5 text-xs text-[var(--color-muted)]">{sub}</div>}
    </Card>
  );
}
